import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { marketplaceAPI } from '../services/api';

function fmt(amount) {
  if (amount == null) return '—';
  return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 }).format(amount);
}

function StatTile({ label, value, sub, accent, icon }) {
  return (
    <div style={{
      background: 'var(--card)', border: '1px solid var(--hairline)',
      borderRadius: 'var(--r-lg)', padding: '18px 20px',
      display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{
          fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase',
          color: 'var(--ink-4)', fontWeight: 600, lineHeight: 1.3,
        }}>
          {label}
        </div>
        {icon && <span style={{ fontSize: 16, opacity: 0.5 }}>{icon}</span>}
      </div>
      <div style={{
        fontFamily: 'var(--serif)', fontSize: 26, fontWeight: 700,
        color: accent || 'var(--forest)', lineHeight: 1.1,
        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
      }}>
        {value}
      </div>
      {sub && (
        <div style={{ fontSize: 11, color: 'var(--ink-4)', lineHeight: 1.4 }}>{sub}</div>
      )}
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <div style={{ fontFamily: 'var(--serif)', fontSize: 16, fontWeight: 600, color: 'var(--forest)', marginBottom: 12, marginTop: 8 }}>
      {children}
    </div>
  );
}

export default function ProDashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsError, setStatsError] = useState(null);

  useEffect(() => {
    marketplaceAPI.getMyProStats()
      .then(r => setStats(r.data))
      .catch(e => setStatsError(e?.response?.data?.detail || 'Could not load stats'))
      .finally(() => setStatsLoading(false));
  }, []);

  return (
    <div className="page-wrap" style={{ maxWidth: 1280 }}>
      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <div style={{ fontFamily: 'var(--serif)', fontSize: 26, fontWeight: 700, color: 'var(--forest)', marginBottom: 4 }}>
          Welcome back, {user?.first_name}
        </div>
        <div style={{ fontSize: 13, color: 'var(--ink-4)' }}>Pro Dashboard · PropAtlas</div>
      </div>

      {/* Stats */}
      {statsLoading && (
        <div style={{ color: 'var(--ink-4)', fontSize: 13, padding: '20px 0' }}>Loading stats…</div>
      )}
      {statsError && (
        <div style={{ background: '#fdecea', border: '1px solid #f5c6c6', borderRadius: 'var(--r)', padding: '12px 16px', color: '#b71c1c', fontSize: 13, marginBottom: 24 }}>
          {statsError}
        </div>
      )}

      {stats && (
        <>
          {/* One dense grid — auto-fit collapses gracefully on narrow
              screens but lays out as a single row at desktop widths. */}
          <SectionTitle>Job Pipeline</SectionTitle>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
            gap: 12, marginBottom: 24,
          }}>
            <StatTile
              label="Pipeline Value"
              value={fmt(stats.pipeline_value)}
              sub={`${stats.pipeline_jobs} active`}
              icon="📋"
            />
            <StatTile
              label="Pending Requests"
              value={stats.pending_requests}
              sub="Awaiting response"
              accent={stats.pending_requests > 0 ? 'var(--terracotta)' : 'var(--forest)'}
              icon="🔔"
            />
            <StatTile
              label="Total Jobs"
              value={stats.total_jobs}
              sub="All statuses"
              icon="📁"
            />
          </div>

          <SectionTitle>Revenue</SectionTitle>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
            gap: 12, marginBottom: 28,
          }}>
            <StatTile
              label="Completed Revenue"
              value={fmt(stats.completed_revenue)}
              sub={`${stats.completed_jobs} completed`}
              accent="var(--moss)"
              icon="✅"
            />
            <StatTile
              label="Avg Job Value"
              value={stats.completed_jobs > 0 ? fmt(stats.completed_revenue / stats.completed_jobs) : '—'}
              sub="Per completed job"
              icon="📊"
            />
          </div>
        </>
      )}

      {/* Quick actions */}
      <SectionTitle>Quick Actions</SectionTitle>
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <button className="btn primary" onClick={() => navigate('/bookings')}>
          View All Jobs
        </button>
        <button className="btn" onClick={() => navigate('/pro/profile')}>
          Edit Profile
        </button>
        <button className="btn" onClick={() => navigate('/marketplace')}>
          Browse Marketplace
        </button>
      </div>
    </div>
  );
}
